import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Copy, Check, AlertCircle, Loader2 } from 'lucide-react';
import { apiClient } from '@/api/ApiClient';
import { useToast } from '@/hooks/use-toast';

interface DebugPromptData {
  id: string;
  messageExchangeId: string;
  fullPrompt: Record<string, any>;
  boyfriendMemory: string[] | null;
  boyfriendFacts: string[];
  userFacts: string[] | null;
  messages: string[];
  ragQuestion: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  createdAt: string;
}

export const DebugPage = () => {
  const { messageExchangeId } = useParams<{ messageExchangeId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [debugData, setDebugData] = useState<DebugPromptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const loadDebugData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getDebugPromptParams(messageExchangeId!);
      setDebugData(data);
    } catch (err: any) {
      console.error('Error loading debug data:', err);
      setError(err.message || 'Failed to load debug data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!messageExchangeId) {
      setLoading(false);
      setError('Missing message ID in URL');
      return;
    }
    void (async () => {
      if (!apiClient.token) {
        await apiClient.hydrateFromStorage();
      }
      try {
        if (!apiClient.token && typeof window !== "undefined" && window.opener) {
          const o = window.opener as Window & { localStorage?: Storage };
          const tok = o.localStorage?.getItem("access_token");
          if (tok) apiClient.setToken(tok);
        }
      } catch {
        /* opener inaccessible */
      }
      loadDebugData();
    })();
  }, [messageExchangeId]);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast({
        title: "Copied to clipboard",
        description: `${fieldName} copied successfully`,
      });
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const formatJson = (obj: any) => {
    return JSON.stringify(obj, null, 2);
  };

  const formatArray = (arr: any[], maxItems: number = 10) => {
    if (arr.length <= maxItems) {
      return arr;
    }
    return [...arr.slice(0, maxItems), `... and ${arr.length - maxItems} more items`];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dreamy flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading debug data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-dreamy flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Error Loading Debug Data</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!debugData) {
    return (
      <div className="min-h-screen bg-gradient-dreamy flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Debug Data Found</h2>
            <p className="text-muted-foreground mb-4">Debug information is not available for this message.</p>
            <Button onClick={() => navigate(-1)} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dreamy">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="hover:bg-primary/10"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Debug Prompt Information</h1>
              <p className="text-muted-foreground">Message Exchange ID: {debugData.messageExchangeId}</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-sm">
            {debugData.model}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Basic Information
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(formatJson({
                  id: debugData.id,
                  messageExchangeId: debugData.messageExchangeId,
                  model: debugData.model,
                  promptTokens: debugData.promptTokens,
                  completionTokens: debugData.completionTokens,
                  createdAt: debugData.createdAt
                }), "Basic Info")}
              >
                {copiedField === "Basic Info" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">ID</label>
                <p className="font-mono text-sm">{debugData.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Message Exchange ID</label>
                <p className="font-mono text-sm">{debugData.messageExchangeId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Model</label>
                <p className="font-mono text-sm">{debugData.model}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="font-mono text-sm">{new Date(debugData.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Message Time</label>
                <p className="font-mono text-sm">{new Date(debugData.createdAt).toLocaleString('cs-CZ', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  timeZone: 'Europe/Prague'
                })}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Prompt Tokens</label>
                <p className="font-mono text-sm">{debugData.promptTokens}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Completion Tokens</label>
                <p className="font-mono text-sm">{debugData.completionTokens}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Full Prompt */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Full Prompt
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(formatJson(debugData.fullPrompt), "Full Prompt")}
              >
                {copiedField === "Full Prompt" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono max-h-96 overflow-y-auto">
              {formatJson(debugData.fullPrompt)}
            </pre>
          </CardContent>
        </Card>

        {/* RAG Question */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              RAG Question
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(debugData.ragQuestion || '', "RAG Question")}
              >
                {copiedField === "RAG Question" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm font-mono whitespace-pre-wrap">
                {debugData.ragQuestion || 'No RAG question available'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* messages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              messages ({debugData.messages?.length || 0})
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard((debugData.messages || []).join('\n'), "messages")}
              >
                {copiedField === "messages" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {formatArray(debugData.messages || [], 20).map((message, index) => (
                <div key={index} className="bg-muted p-3 rounded-lg text-sm">
                  {typeof message === 'string' ? message : `... and ${(debugData.messages || []).length - 20} more items`}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* boyfriendMemory */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              boyfriendMemory ({debugData.boyfriendMemory?.length || 0})
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard((debugData.boyfriendMemory || []).join('\n'), "boyfriendMemory")}
              >
                {copiedField === "boyfriendMemory" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {formatArray(debugData.boyfriendMemory || [], 20).map((memory, index) => (
                <div key={index} className="bg-muted p-3 rounded-lg text-sm">
                  {typeof memory === 'string' ? memory : `... and ${(debugData.boyfriendMemory || []).length - 20} more items`}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* boyfriendFacts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              boyfriendFacts ({debugData.boyfriendFacts?.length || 0})
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard((debugData.boyfriendFacts || []).join('\n'), "boyfriendFacts")}
              >
                {copiedField === "boyfriendFacts" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {formatArray(debugData.boyfriendFacts || [], 20).map((fact, index) => (
                <div key={index} className="bg-muted p-3 rounded-lg text-sm">
                  {typeof fact === 'string' ? fact : `... and ${(debugData.boyfriendFacts || []).length - 20} more items`}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* userFacts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              userFacts ({debugData.userFacts?.length || 0})
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard((debugData.userFacts || []).join('\n'), "userFacts")}
              >
                {copiedField === "userFacts" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {formatArray(debugData.userFacts || [], 20).map((fact, index) => (
                <div key={index} className="bg-muted p-3 rounded-lg text-sm">
                  {typeof fact === 'string' ? fact : `... and ${(debugData.userFacts || []).length - 20} more items`}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};
